import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("timeout"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
      } catch { /* keep trying */ }
      if (tries.current >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, refresh]);

  return (
    <main data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-28 text-center">
      {state === "checking" && (
        <div className="fade-up">
          <Loader2 size={40} className="mx-auto text-[#D4AF37] animate-spin" />
          <p className="mt-6 text-[#B4A897] italic-serif text-xl">Sealing your journey…</p>
        </div>
      )}
      {state === "paid" && (
        <div className="fade-up">
          <CheckCircle2 size={56} className="mx-auto text-[#D4AF37]" />
          <h1 className="mt-6 serif text-4xl text-[#F7F1E6]">Welcome, fellow traveler.</h1>
          <p className="mt-4 text-[#B4A897] text-lg leading-relaxed">
            Your path is open. May what you find here return you, gently, to the Light within.
          </p>
          <Link to="/journal" data-testid="success-to-journal"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-8 py-3.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 gold-glow">
            Begin the inner work <ArrowRight size={18} />
          </Link>
        </div>
      )}
      {state === "timeout" && (
        <div className="fade-up">
          <h1 className="serif text-3xl text-[#F7F1E6]">Almost there…</h1>
          <p className="mt-4 text-[#B4A897] leading-relaxed">
            Your payment is being confirmed. It can take a moment — refresh this page, or check back shortly.
          </p>
          <Link to="/pricing" className="mt-8 inline-block text-[#D4AF37] hover:underline">Back to plans</Link>
        </div>
      )}
    </main>
  );
}
